/**
 * PublicDashboardPage — share-by-token public dashboard view (no auth required).
 */

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { StatusSummaryWidget } from "../components/organisms/widgets/StatusSummaryWidget";
import { MetricChartWidget } from "../components/organisms/widgets/MetricChartWidget";
import { IncidentListWidget } from "../components/organisms/widgets/IncidentListWidget";
import { OnCallWidget } from "../components/organisms/widgets/OnCallWidget";
import { SLOBurnWidget } from "../components/organisms/widgets/SLOBurnWidget";
import { apiFetch } from "../lib/config";

interface WidgetItem {
  id: string;
  widget_type: string;
  position: number;
  title: string;
  config: Record<string, unknown>;
}

interface DashboardData {
  id: string;
  name: string;
  widgets: WidgetItem[];
}

function WidgetRenderer({ widget }: { widget: WidgetItem }) {
  switch (widget.widget_type) {
    case "status_summary":
      return <StatusSummaryWidget config={widget.config} />;
    case "metric_chart":
      return <MetricChartWidget config={widget.config} />;
    case "incident_list":
      return <IncidentListWidget config={widget.config} />;
    case "on_call_status":
      return <OnCallWidget config={widget.config} />;
    case "slo_burn":
      return <SLOBurnWidget config={widget.config} />;
    default:
      return (
        <p style={{ color: "var(--aptean-text-secondary)", margin: 0 }}>
          Unknown widget type: {widget.widget_type}
        </p>
      );
  }
}

export function PublicDashboardPage() {
  const { token } = useParams<{ token: string }>();
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!token) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    apiFetch(`/api/v1/dashboards/public/${token}/`)
      .then((r) => {
        if (r.status === 404) {
          setNotFound(true);
          return null;
        }
        return r.json();
      })
      .then((d) => {
        if (d) setDashboard(d as DashboardData);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "var(--aptean-bg, #13131f)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <p style={{ color: "var(--aptean-text-secondary)" }}>Loading dashboard...</p>
      </div>
    );
  }

  if (notFound || !dashboard) {
    return (
      <div style={{ minHeight: "100vh", background: "var(--aptean-bg, #13131f)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <h1 style={{ color: "var(--aptean-text-primary)", margin: "0 0 8px" }}>Dashboard Not Found</h1>
        <p style={{ color: "var(--aptean-text-secondary)" }}>
          This dashboard link is invalid or the dashboard is no longer public.
        </p>
      </div>
    );
  }

  const sorted = [...dashboard.widgets].sort((a, b) => a.position - b.position);

  return (
    <div style={{ minHeight: "100vh", background: "var(--aptean-bg, #13131f)" }}>
      {/* Aptean header */}
      <header
        style={{
          background: "var(--aptean-surface-1, #1c1c2e)",
          borderBottom: "1px solid var(--aptean-border, #2a2a3a)",
          padding: "16px 24px",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <span
          style={{
            fontSize: "1.2rem",
            fontWeight: 700,
            color: "var(--aptean-accent, #54B3BE)",
            fontFamily: "var(--aptean-font-heading, sans-serif)",
          }}
        >
          Aptean
        </span>
        <span style={{ color: "var(--aptean-text-secondary)", fontSize: "0.9rem" }}>
          Service Status
        </span>
      </header>

      <main style={{ padding: 24 }}>
        <h1 style={{ color: "var(--aptean-text-primary)", margin: "0 0 24px", fontSize: "1.5rem" }}>
          {dashboard.name}
        </h1>

        <div style={{ display: "grid", gap: 16 }}>
          {sorted.map((w) => (
            <div
              key={w.id}
              className="aptean-card"
              style={{ padding: 16 }}
            >
              {w.title && (
                <h3 style={{ margin: "0 0 12px", fontSize: "0.9rem", color: "var(--aptean-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  {w.title}
                </h3>
              )}
              <WidgetRenderer widget={w} />
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
